import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, ChevronRight, Download, Edit3, Eye, Info, KeyRound, Paperclip, Plus, Save, Trash2, Upload, X } from 'lucide-react';
import { useApiClient } from '../auth/useApiClient';
import CatalogueAdminPage from './CatalogueAdminPage';

const emptyStarter = { label: '', message: '' };
const orderVisibleAgents = (agentList = []) => [...agentList]
  .filter((agent) => agent.id !== 'pathway-verifier' && agent.id !== 'programme-insight')
  .sort((a, b) => {
    if (a.id === 'email-drafter' && b.id !== 'email-drafter') return 1;
    if (b.id === 'email-drafter' && a.id !== 'email-drafter') return -1;
    return (a.name || '').localeCompare(b.name || '');
  });

const SettingsPage = () => {
  const { request } = useApiClient();
  const [activeTab, setActiveTab] = useState('agents');
  const [agents, setAgents] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [draft, setDraft] = useState(null);
  const [status, setStatus] = useState('');
  const [uploadRows, setUploadRows] = useState([]);
  const [apiKeyStatus, setApiKeyStatus] = useState({ configured: false, masked: '' });
  const [isEditingApiKey, setIsEditingApiKey] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const [apiKeyMessage, setApiKeyMessage] = useState('');
  const [isSavingApiKey, setIsSavingApiKey] = useState(false);
  const [settingsModelMenuOpen, setSettingsModelMenuOpen] = useState('');
  const [hoveredSettingsModel, setHoveredSettingsModel] = useState('');
  const [verifierDraft, setVerifierDraft] = useState(null);

  const visibleAgents = orderVisibleAgents(agents);
  const selectedAgent = visibleAgents.find((agent) => agent.id === selectedAgentId) || visibleAgents[0] || null;
  const isPathwayAgents = draft?.id === 'pathway-recommender';
  const pathwayVerifierAgent = agents.find((agent) => agent.id === 'pathway-verifier') || null;

  const displayAgentName = (agent) => (
    agent?.id === 'pathway-recommender'
      ? 'Pathway Recommender and Verifier Agents'
      : agent?.name || ''
  );

  const displayAgentDescription = (agent) => (
    agent?.id === 'pathway-recommender'
      ? 'Creates programme pathways and verifies them before adviser review.'
      : agent?.description || ''
  );

  const loadApiKeyStatus = useCallback(async () => {
    const response = await request('/api/admin/openai-key');
    if (!response.ok) return;
    const data = await response.json();
    setApiKeyStatus({
      configured: Boolean(data.configured),
      masked: data.masked || ''
    });
  }, [request]);

  const loadSettings = useCallback(async () => {
    const [agentsResponse, modelsResponse] = await Promise.all([
      request('/api/admin/agents'),
      request('/api/openai/models')
    ]);

    if (agentsResponse.ok) {
      const data = await agentsResponse.json();
      const nextAgents = Array.isArray(data.agents) ? data.agents : [];
      const nextVisibleAgents = orderVisibleAgents(nextAgents);
      setAgents(nextAgents);
      if ((!selectedAgentId || !nextVisibleAgents.some((agent) => agent.id === selectedAgentId)) && nextVisibleAgents[0]) {
        setSelectedAgentId(nextVisibleAgents[0].id);
        setDraft(nextVisibleAgents[0]);
      }
    }

    if (modelsResponse.ok) {
      const data = await modelsResponse.json();
      setModels(Array.isArray(data.models) ? data.models : []);
    }
  }, [request, selectedAgentId]);

  useEffect(() => {
    loadSettings();
    loadApiKeyStatus();
  }, [loadApiKeyStatus, loadSettings]);

  useEffect(() => {
    if (selectedAgent) {
      setDraft({
        ...selectedAgent,
        conversationStarters: Array.isArray(selectedAgent.conversationStarters)
          ? selectedAgent.conversationStarters
          : []
      });
    }
  }, [selectedAgent]);

  useEffect(() => {
    if (pathwayVerifierAgent) {
      setVerifierDraft({
        ...pathwayVerifierAgent,
        conversationStarters: Array.isArray(pathwayVerifierAgent.conversationStarters)
          ? pathwayVerifierAgent.conversationStarters
          : []
      });
    }
  }, [pathwayVerifierAgent]);

  const updateDraft = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const updateVerifierDraft = (field, value) => {
    setVerifierDraft((prev) => ({ ...prev, [field]: value }));
  };

  const updateStarter = (index, field, value) => {
    setDraft((prev) => {
      const starters = [...(prev.conversationStarters || [])];
      starters[index] = { ...starters[index], [field]: value };
      return { ...prev, conversationStarters: starters };
    });
  };

  const addStarter = () => {
    setDraft((prev) => ({
      ...prev,
      conversationStarters: [...(prev.conversationStarters || []), { ...emptyStarter }]
    }));
  };

  const removeStarter = (index) => {
    setDraft((prev) => ({
      ...prev,
      conversationStarters: (prev.conversationStarters || []).filter((_, starterIndex) => starterIndex !== index)
    }));
  };

  const buildAgentSavePayload = (agentDraft) => {
    const conversationStarters = (agentDraft.conversationStarters || [])
      .map((starter) => ({
        label: (starter.label || '').trim(),
        message: (starter.message || '').trim()
      }))
      .filter((starter) => starter.label && starter.message);

    return {
      systemPrompt: agentDraft.systemPrompt,
      defaultModel: agentDraft.defaultModel,
      reasoningEffort: agentDraft.reasoningEffort,
      vectorStoreId: agentDraft.vectorStoreId,
      isEnabled: agentDraft.isEnabled,
      conversationStarters
    };
  };

  const saveAgentDraft = (agentDraft) => request(`/api/admin/agents/${agentDraft.id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(buildAgentSavePayload(agentDraft))
  });

  const saveAgent = async () => {
    if (!draft) return;
    setStatus('Saving...');

    const requests = [saveAgentDraft(draft)];
    if (isPathwayAgents && verifierDraft) {
      requests.push(saveAgentDraft(verifierDraft));
    }

    const responses = await Promise.all(requests);
    const saved = responses.every((response) => response.ok);

    setStatus(saved ? 'Saved' : 'Could not save settings');
    await loadSettings();
  };

  const uploadKnowledgeFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !draft) return;

    const uploadId = `${file.name}-${Date.now()}`;
    setUploadRows((prev) => [
      ...prev,
      { id: uploadId, filename: file.name, progress: 12, stage: 'Uploading' }
    ]);
    setStatus('');

    let progressTimer = window.setInterval(() => {
      setUploadRows((prev) => prev.map((row) => (
        row.id === uploadId
          ? { ...row, progress: Math.min(row.progress + 8, row.stage === 'Uploading' ? 68 : 92) }
          : row
      )));
    }, 400);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await request(`/api/admin/agents/${draft.id}/files`, {
        method: 'POST',
        body: formData
      });

      setUploadRows((prev) => prev.map((row) => (
        row.id === uploadId ? { ...row, progress: 76, stage: 'Indexing' } : row
      )));

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      await loadSettings();
      setUploadRows((prev) => prev.map((row) => (
        row.id === uploadId ? { ...row, progress: 100, stage: 'Indexed' } : row
      )));
      window.setTimeout(() => {
        setUploadRows((prev) => prev.filter((row) => row.id !== uploadId));
      }, 1800);
    } catch (error) {
      setUploadRows((prev) => prev.map((row) => (
        row.id === uploadId ? { ...row, progress: 100, stage: 'Failed' } : row
      )));
      setStatus('Could not add file');
    } finally {
      window.clearInterval(progressTimer);
    }
  };

  const deleteKnowledgeFile = async (fileId) => {
    if (!draft || !window.confirm('Remove this knowledge file?')) return;

    const response = await request(`/api/admin/agents/${draft.id}/files/${fileId}`, {
      method: 'DELETE'
    });
    setStatus(response.ok ? 'File removed' : 'Could not remove file');
    await loadSettings();
  };

  const openKnowledgeFile = async (file, disposition = 'inline') => {
    if (!draft || !file?.id) return;
    const response = await request(`/api/admin/agents/${draft.id}/files/${file.id}/download?disposition=${disposition}`);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      setStatus(data.message || (disposition === 'inline' ? 'Could not open file' : 'Could not download file'));
      return;
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    if (disposition === 'inline') {
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(url), 5000);
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.download = file.filename || 'knowledge-file';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const saveApiKey = async () => {
    const trimmedKey = newApiKey.trim();
    if (!trimmedKey) return;

    if (apiKeyStatus.configured) {
      const confirmed = window.confirm(
        'Changing the OpenAI API key may switch the app to a different OpenAI project. Knowledge file/vector-store references and OpenAI chat memory will be reset; saved chats will remain and rebuild from local history. Continue?'
      );
      if (!confirmed) return;
    }

    setIsSavingApiKey(true);
    setApiKeyMessage('Testing API key before saving...');
    const response = await request('/api/admin/openai-key', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ apiKey: trimmedKey })
    });

    if (response.ok) {
      const data = await response.json();
      setApiKeyStatus({ configured: Boolean(data.configured), masked: data.masked || '' });
      setNewApiKey('');
      setIsEditingApiKey(false);
      const cleared = data.clearedOpenAIState;
      if (data.keyRotated && cleared) {
        setApiKeyMessage(
          `API key tested and saved. Reset ${cleared.chatThreadsReset || 0} chat memories and cleared ${cleared.knowledgeFilesCleared || 0} knowledge file references. Upload knowledge files again for the new key.`
        );
        await loadSettings();
      } else {
        setApiKeyMessage('API key tested and saved.');
      }
    } else {
      setApiKeyMessage('The new API key did not validate, so it was not saved.');
    }

    setIsSavingApiKey(false);
  };

  const getModelOptions = (modelId) => (
    modelId && !models.some((model) => model.id === modelId)
      ? [{ id: modelId, label: modelId, supportsReasoning: false, reasoningEfforts: [] }, ...models]
      : models
  );
  const showConversationStarters = draft?.id === 'knowledge-assistant';

  const modelLabel = (modelId, reasoningEffort) => {
    const options = getModelOptions(modelId);
    const selected = options.find((model) => model.id === modelId);
    return selected
      ? `${selected.label}${reasoningEffort ? ` / ${reasoningEffort}` : ''}`
      : 'Select model';
  };

  const selectSettingsModel = (target, model, effort = '') => {
    const updater = target === 'verifier' ? updateVerifierDraft : updateDraft;
    updater('defaultModel', model.id);
    if (model.supportsReasoning) {
      updater('reasoningEffort', effort || model.reasoningEfforts?.[1] || model.reasoningEfforts?.[0] || '');
    } else {
      updater('reasoningEffort', '');
    }
    setSettingsModelMenuOpen('');
    setHoveredSettingsModel('');
  };

  const renderModelPicker = ({ label, target, modelId, reasoningEffort }) => {
    const options = getModelOptions(modelId);
    const isOpen = settingsModelMenuOpen === target;

    return (
      <div>
        <label className="input-label">{label}</label>
        <div className="model-menu settings-model-menu">
          <button
            type="button"
            className="model-menu-trigger"
            onClick={() => setSettingsModelMenuOpen((previous) => (previous === target ? '' : target))}
            aria-haspopup="menu"
            aria-expanded={isOpen}
          >
            <span>{modelLabel(modelId, reasoningEffort)}</span>
            <ChevronDown size={14} />
          </button>
          {isOpen && (
            <div className="model-menu-popover" role="menu">
              {options.length === 0 && <div className="model-menu-empty">No models available</div>}
              {options.map((model) => (
                <div
                  key={model.id}
                  className="model-menu-row"
                  onMouseEnter={() => setHoveredSettingsModel(model.id)}
                  onFocus={() => setHoveredSettingsModel(model.id)}
                >
                  <button type="button" onClick={() => selectSettingsModel(target, model)}>
                    <span>{model.label}</span>
                    {modelId === model.id && <Check size={13} />}
                  </button>
                  {model.supportsReasoning && (
                    <div className="reasoning-flyout-wrap">
                      <button
                        type="button"
                        className="reasoning-flyout-trigger"
                        aria-label={`Choose reasoning level for ${model.label}`}
                        onMouseEnter={() => setHoveredSettingsModel(model.id)}
                        onFocus={() => setHoveredSettingsModel(model.id)}
                      >
                        <ChevronRight size={14} />
                      </button>
                      {hoveredSettingsModel === model.id && (
                        <div className="reasoning-flyout">
                          {model.reasoningEfforts.map((effort) => (
                            <button
                              key={`${model.id}-${effort}`}
                              type="button"
                              onClick={() => selectSettingsModel(target, model, effort)}
                              className={modelId === model.id && reasoningEffort === effort ? 'active' : ''}
                            >
                              {effort}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="page settings-page fill-page">
      <div className="page-header settings-page-header">
        <div className="section-label">Settings</div>
        <div className="settings-tabs" role="tablist" aria-label="Settings sections">
          <button type="button" className={activeTab === 'agents' ? 'active' : ''} onClick={() => setActiveTab('agents')}>
            Agents
          </button>
          <button type="button" className={activeTab === 'api-key' ? 'active' : ''} onClick={() => setActiveTab('api-key')}>
            API Key
          </button>
          <button type="button" className={activeTab === 'catalogue' ? 'active' : ''} onClick={() => setActiveTab('catalogue')}>
            Catalogue
          </button>
        </div>
      </div>

      {activeTab === 'agents' && (
        <div className="settings-layout fill-page-grid">
          <aside className="card settings-agent-list schema-scroll">
            {visibleAgents.map((agent) => (
              <button
                key={agent.id}
                type="button"
                className={`settings-agent-tab ${agent.id === selectedAgentId ? 'active' : ''}`}
                onClick={() => setSelectedAgentId(agent.id)}
              >
                <span>{displayAgentName(agent)}</span>
                <small>{displayAgentDescription(agent)}</small>
              </button>
            ))}
          </aside>

          {draft && (
            <div className="card settings-editor schema-scroll">
              <div className="recommendation-section-header">
                <div>
                  <div className="section-label">{displayAgentName(draft)}</div>
                  <p className="assistant-info">{displayAgentDescription(draft)}</p>
                </div>
                <button type="button" className="btn-primary" onClick={saveAgent}>
                  <Save size={16} />
                  Save
                </button>
              </div>

              <div className="settings-grid">
                {renderModelPicker({
                  label: isPathwayAgents ? 'Recommender Model' : 'Default Model',
                  target: 'primary',
                  modelId: draft.defaultModel,
                  reasoningEffort: draft.reasoningEffort
                })}
                {isPathwayAgents && verifierDraft && (
                  <label className="settings-checkbox-card">
                    <input
                      type="checkbox"
                      checked={verifierDraft.isEnabled !== false}
                      onChange={(event) => updateVerifierDraft('isEnabled', event.target.checked)}
                    />
                    <span className="settings-checkbox-mark">
                      {verifierDraft.isEnabled !== false && <Check size={13} />}
                    </span>
                    <span className="settings-checkbox-copy">
                      <span className="settings-checkbox-title">
                        <strong>Use verifier in pathway generation</strong>
                        <span className="settings-info-icon" tabIndex={0} aria-label="When enabled, the verifier agent checks pathway logic before recommendations are shown.">
                          <Info size={14} />
                          <span className="settings-info-tooltip">When enabled, the verifier agent checks pathway logic before recommendations are shown.</span>
                        </span>
                      </span>
                    </span>
                  </label>
                )}
              </div>

              <div className="settings-field">
                <label className="input-label">{isPathwayAgents ? 'Recommender System Prompt' : 'System Prompt'}</label>
                <textarea
                  value={draft.systemPrompt || ''}
                  onChange={(e) => updateDraft('systemPrompt', e.target.value)}
                  rows={12}
                />
              </div>

              {isPathwayAgents && verifierDraft && (
                <div className="settings-subpanel settings-agent-subpanel">
                  <div className="recommendation-section-header">
                    <div>
                      <div className="section-label">Verifier Agent</div>
                      <p className="assistant-info">Reviews pathway composition, certificate logic, and profile fit after deterministic catalogue checks pass.</p>
                    </div>
                  </div>
                  <div className="settings-grid">
                    {renderModelPicker({
                      label: 'Verifier Model',
                      target: 'verifier',
                      modelId: verifierDraft.defaultModel,
                      reasoningEffort: verifierDraft.reasoningEffort
                    })}
                  </div>
                  <div className="settings-field">
                    <label className="input-label">Verifier System Prompt</label>
                    <textarea
                      value={verifierDraft.systemPrompt || ''}
                      onChange={(e) => updateVerifierDraft('systemPrompt', e.target.value)}
                      rows={10}
                    />
                  </div>
                </div>
              )}

              {showConversationStarters && (
                <div className="settings-subpanel">
                  <div className="recommendation-section-header">
                    <div>
                      <div className="section-label">Conversation Starters</div>
                      <p className="assistant-info">Each starter has a short label and the full message sent to the Query agent.</p>
                    </div>
                    <button type="button" className="btn-primary" onClick={addStarter}>
                      <Plus size={16} />
                      Add Starter
                    </button>
                  </div>
                  <div className="conversation-starter-editor-list">
                    {(draft.conversationStarters || []).map((starter, index) => (
                      <div key={`starter-${index}`} className="conversation-starter-editor-row">
                        <div className="conversation-starter-fields">
                          <input
                            value={starter.label || ''}
                            onChange={(e) => updateStarter(index, 'label', e.target.value)}
                            placeholder="Label shown"
                          />
                          <textarea
                            value={starter.message || ''}
                            onChange={(e) => updateStarter(index, 'message', e.target.value)}
                            placeholder="Message sent"
                            rows={1}
                          />
                        </div>
                        <button type="button" className="icon-btn" onClick={() => removeStarter(index)} aria-label="Remove starter">
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="settings-subpanel">
                <div className="recommendation-section-header">
                  <div>
                    <div className="section-label">{isPathwayAgents ? 'Shared Knowledge Files' : 'Knowledge Files'}</div>
                    {isPathwayAgents && <p className="assistant-info">Used by both the recommender and verifier agents.</p>}
                  </div>
                  <label className="btn-primary file-upload-label">
                    <Upload size={16} />
                    Add File
                    <input type="file" onChange={uploadKnowledgeFile} />
                  </label>
                </div>
                <div className="knowledge-file-list">
                  {uploadRows.map((row) => (
                    <div key={row.id} className={`knowledge-file-row upload-progress-row ${row.stage.toLowerCase()}`}>
                      <span>
                        <Paperclip size={14} />
                        {row.filename}
                      </span>
                      <div className="upload-progress-wrap">
                        <div className="upload-progress-track">
                          <div className="upload-progress-fill" style={{ width: `${row.progress}%` }} />
                        </div>
                        <small>{row.stage}</small>
                      </div>
                    </div>
                  ))}
                  {(draft.knowledgeFiles || []).map((file) => (
                    <div key={file.id} className="knowledge-file-row">
                      <span>
                        <Paperclip size={14} />
                        {file.filename}
                      </span>
                      <div className="knowledge-file-actions">
                        <button type="button" className="icon-btn" onClick={() => openKnowledgeFile(file, 'inline')} aria-label="View file" disabled={!file.isDownloadable}>
                          <Eye size={14} />
                        </button>
                        <button type="button" className="icon-btn" onClick={() => openKnowledgeFile(file, 'download')} aria-label="Download file" disabled={!file.isDownloadable}>
                          <Download size={14} />
                        </button>
                        <button type="button" className="icon-btn" onClick={() => deleteKnowledgeFile(file.id)} aria-label="Remove file">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                  {uploadRows.length === 0 && (!draft.knowledgeFiles || draft.knowledgeFiles.length === 0) && (
                    <p className="assistant-info">No files attached.</p>
                  )}
                </div>
              </div>
              {status && <p className="participant-copy-status">{status}</p>}
            </div>
          )}
        </div>
      )}

      {activeTab === 'api-key' && (
        <div className="card settings-editor api-key-panel">
          <div className="recommendation-section-header">
            <div>
              <div className="section-label">OpenAI API Key</div>
              <p className="assistant-info">Changing this affects every OpenAI-backed feature in the app.</p>
            </div>
            <KeyRound size={20} />
          </div>

          <div className="api-key-edit-wrap">
            <div className="api-key-field">
              <input value={apiKeyStatus.configured ? apiKeyStatus.masked : 'No API key configured'} readOnly />
              <button type="button" className="icon-btn" onClick={() => setIsEditingApiKey(true)} aria-label="Edit API key">
                <Edit3 size={15} />
              </button>
            </div>

            {isEditingApiKey && (
              <div className="api-key-inline-popover">
                <div className="api-key-warning">
                  <AlertTriangle size={16} />
                  <span>Saving a different key resets project-scoped knowledge file references and OpenAI chat memory. Saved chats remain and rebuild from local history.</span>
                </div>
                <input
                  type="password"
                  value={newApiKey}
                  onChange={(e) => setNewApiKey(e.target.value)}
                  placeholder="Paste new API key"
                />
                <div className="api-key-actions">
                  <button type="button" className="btn-primary" onClick={saveApiKey} disabled={isSavingApiKey || !newApiKey.trim()}>
                    <Check size={16} />
                    {isSavingApiKey ? 'Testing...' : 'Save Key'}
                  </button>
                  <button type="button" className="text-btn" onClick={() => { setIsEditingApiKey(false); setNewApiKey(''); }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {apiKeyMessage && <p className="participant-copy-status">{apiKeyMessage}</p>}
        </div>
      )}

      {activeTab === 'catalogue' && (
        <CatalogueAdminPage embedded />
      )}
    </section>
  );
};

export default SettingsPage;
